import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { normalizeErrorString } from '@medplum/core'
import { useOrpc, type OrpcClient } from '@/hooks/useClient'
import type { BRIEFING_DISMISS_REASONS } from '@/model/middleware-types'

export type BriefingDay = Awaited<ReturnType<OrpcClient['briefingDay']>>
export type BriefingItem = BriefingDay['items'][number]
export type BriefingAppointment = BriefingDay['appointments'][number]
export type BriefingDismissReason = (typeof BRIEFING_DISMISS_REASONS)[number]

const DAY_MS = 24 * 60 * 60 * 1000

export type BriefingAction =
  | { action: 'dismiss'; reason: BriefingDismissReason }
  | { action: 'remind'; remindUntil: string }
  | { action: 'restore'; undo?: boolean }

/**
 * Today's briefing for the signed-in provider, read once per Home visit (60 s stale) and
 * again when the soonest pending reminder comes due or the local date changes, plus the
 * per-item dismiss / remind / restore action. The action hides or shows the item at once
 * and rolls only that item back if the save fails, so actions on different items never
 * undo each other.
 */
export function useBriefingDay(enabled: boolean) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [date, setDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  useEffect(() => {
    const sync = () => setDate(dayjs().format('YYYY-MM-DD'))
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync()
    }
    const midnight = setTimeout(sync, dayjs().add(1, 'day').startOf('day').diff(dayjs()) + 1000)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(midnight)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [date])
  const input = useMemo(
    () => ({ date, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    [date],
  )
  const queryKey = orpc.briefingDay.queryKey({ input })
  const query = useQuery({
    ...orpc.briefingDay.queryOptions({ input }),
    enabled,
    staleTime: 60_000,
  })

  const { data, refetch } = query
  useEffect(() => {
    if (!enabled || !data) return
    const now = Date.now()
    const soonest = Math.min(
      ...data.items.map((item) =>
        item.state?.hidden === 'reminded' ? Date.parse(item.state.remindUntil) : Infinity,
      ),
    )
    if (!Number.isFinite(soonest) || soonest <= now) return
    const timer = setTimeout(() => void refetch(), Math.min(soonest - now + 1000, DAY_MS))
    return () => clearTimeout(timer)
  }, [enabled, data, refetch])

  // Only the version the action was about: a refetch may already hold a newer version of the
  // item (a new message in the thread), which a save for the old one must not hide.
  const patchItem = useCallback(
    (itemId: string, version: string, state: BriefingItem['state']) =>
      queryClient.setQueryData<BriefingDay>(queryKey, (day) =>
        day
          ? {
              ...day,
              items: day.items.map((i) =>
                i.id === itemId && i.version === version ? { ...i, state } : i,
              ),
            }
          : day,
      ),
    [queryClient, queryKey],
  )

  const mutation = useMutation(
    orpc.briefingItemState.mutationOptions({
      onMutate: async (vars) => {
        await queryClient.cancelQueries({ queryKey })
        const previous = queryClient
          .getQueryData<BriefingDay>(queryKey)
          ?.items.find((i) => i.id === vars.itemId && i.version === vars.version)?.state
        const at = new Date().toISOString()
        patchItem(
          vars.itemId,
          vars.version,
          vars.action === 'dismiss' && vars.reason
            ? { hidden: 'dismissed', reason: vars.reason, at }
            : vars.action === 'remind' && vars.remindUntil
              ? { hidden: 'reminded', remindUntil: vars.remindUntil, at }
              : null,
        )
        return { previous: previous ?? null }
      },
      onSuccess: (data, vars) => patchItem(vars.itemId, vars.version, data.state),
      onError: (error, vars, ctx) => {
        if (ctx) patchItem(vars.itemId, vars.version, ctx.previous)
        showNotification({
          color: 'red',
          title: 'Unable to update the briefing',
          message: normalizeErrorString(error),
        })
      },
    }),
  )

  const { mutateAsync } = mutation
  // One save at a time per item, in click order: a Restore clicked while its Dismiss is still
  // in flight must not reach the server first and then be overwritten by the Dismiss.
  const queues = useRef(new Map<string, Promise<unknown>>())
  const setItemState = useCallback(
    (item: BriefingItem, change: BriefingAction) => {
      const previous = queues.current.get(item.id) ?? Promise.resolve()
      const next = previous
        .catch(() => undefined)
        .then(() =>
          mutateAsync({
            itemId: item.id,
            version: item.version,
            kind: item.kind,
            patientId: item.patient.id,
            sourceId: item.sourceId,
            ...change,
          }),
        )
      queues.current.set(item.id, next)
      next
        .finally(() => {
          if (queues.current.get(item.id) === next) queues.current.delete(item.id)
        })
        .catch(() => undefined)
      return next
    },
    [mutateAsync],
  )

  // Marks the briefing stale without fetching now: opening an item leaves Home, and what the
  // provider does there (reads the thread, signs the note) shows when they come back.
  const markStale = useCallback(
    () => void queryClient.invalidateQueries({ queryKey, refetchType: 'none' }),
    [queryClient, queryKey],
  )

  return { query, setItemState, markStale }
}
